import { Module } from 'vuex'
import { fetchCurrentUser } from '@/api_client'
import { AuthStateInterface } from '@/models/store/AuthState.interface'
import { RootStateInterface } from '@/models/store/RootState.interface'
import {
  PermissionMap,
  Role,
  UserInterface
} from '@/models/users/User.interface'

// Backend feature key. Single-word keys (template kinds, image, surveys) are
// unaffected by the API case converter; multi-word keys become camelCase
// (user_management -> userManagement, calendar_event -> calendarEvent).
export const USER_MANAGEMENT_FEATURE = 'userManagement'
// Features that exist in the permission map but aren't template kinds, so they
// don't count toward "can create a template".
export const NON_TEMPLATE_FEATURES = [
  'image',
  'calendarEvent',
  'surveys',
  USER_MANAGEMENT_FEATURE
]

const ROLE_RANK: Record<Role, number> = { read: 1, write: 2, manage: 3 }
const ACTION_ROLES: Record<string, Role> = {
  view: 'read',
  create: 'write',
  edit: 'write',
  copy: 'write',
  export: 'write',
  delete: 'manage',
  manage: 'manage'
}

const STORAGE_KEY = 'user'

const loadUser = (): UserInterface | null => {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as UserInterface
  } catch {
    return null
  }
}

const authModule: Module<AuthStateInterface, RootStateInterface> = {
  namespaced: true,
  state: () => ({ user: loadUser() }),
  mutations: {
    // Keep localStorage in sync with the store so the rehydrated state (and the
    // permission map it drives) survives a refresh — e.g. after a self-edit.
    setUser(state, user: UserInterface) {
      state.user = user
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user))
    },
    clearUser(state) {
      state.user = null
      localStorage.removeItem(STORAGE_KEY)
    }
  },
  actions: {
    // Loads the current user + permission map from the backend. Used in
    // development to populate permissions without a Cognito login.
    async loadCurrentUser({ commit }) {
      const user: UserInterface = await fetchCurrentUser()
      commit('setUser', user)
    }
  },
  getters: {
    permissions: (state): PermissionMap => state.user?.permissions ?? {},
    // Method-style getter: can('email', 'edit'), can('userManagement', 'manage').
    can:
      (_state, getters) =>
      (feature: string, action = 'view'): boolean => {
        const required = ACTION_ROLES[action] ?? 'read'
        const role: Role = getters.permissions[feature] ?? 'read'
        return ROLE_RANK[role] >= ROLE_RANK[required]
      },
    canManageUsers: (_state, getters): boolean =>
      getters.can(USER_MANAGEMENT_FEATURE, 'manage'),
    // The "new template" action is allowed when the user can create at least
    // one template kind (kind is chosen later in the editor).
    canCreateAnyTemplate: (state, getters): boolean =>
      Object.keys(state.user?.permissions ?? {})
        .filter((key) => !NON_TEMPLATE_FEATURES.includes(key))
        .some((kind) => getters.can(kind, 'create'))
  }
}

export default authModule
